import React, { useEffect, useState } from 'react';
import { HelpCircle } from 'lucide-react';
import { api } from '../lib/api';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

export const PRIO_STYLES = {
  A: 'border-red-500/50 bg-red-500/15 text-red-300',
  B: 'border-amber-500/50 bg-amber-500/15 text-amber-300',
  C: 'border-sky-500/50 bg-sky-500/15 text-sky-300',
  PASS: 'border-zinc-500/50 bg-zinc-500/15 text-zinc-300',
};
export const PRIO_LABEL = (p) => (p === 'PASS' ? 'Pass (Bench)' : `Prio ${p}`);

// Mirrors backend prio_for(): 'A' | 'B' | 'C' | 'PASS'. Base level from counting criteria (A rule, else B, else PASS);
// ticked knockout → PASS; succession box (if configured) must be ticked: A without → C, B without → PASS.
export const computePrioWith = (ids, rules, criteria) => {
  const ticked = new Set(ids || []);
  if (!rules) return ticked.size >= 4 ? 'A' : ticked.size >= 2 ? 'B' : 'PASS';
  const kind = Object.fromEntries((criteria || []).map(c => [c.id, c.kind || 'count']));
  const counting = [...ticked].filter(c => (kind[c] || 'count') === 'count');
  let base = 'PASS';
  for (const p of ['A', 'B']) {
    const r = rules[p] || {};
    const scope = (r.criteria || []).filter(c => (kind[c] || 'count') === 'count');
    const need = Number(r.min || 0);
    if (!scope.length || need < 1) continue;
    if (scope.filter(c => counting.includes(c)).length >= Math.min(need, scope.length)) { base = p; break; }
  }
  if (base === 'PASS' || [...ticked].some(c => kind[c] === 'knockout')) return 'PASS';
  const succIds = Object.keys(kind).filter(k => kind[k] === 'succession');
  const succession = succIds.length === 0 || succIds.some(k => ticked.has(k));
  if (base === 'A') return succession ? 'A' : 'C';
  return succession ? 'B' : 'PASS';
};
export const computePrio = (ids) => computePrioWith(ids, null);


// Admin config (criteria, rules, descriptions) — fetched once per page load and shared.
let _cfgPromise = null;
export const invalidatePrioConfig = () => { _cfgPromise = null; };
export function usePrioConfig() {
  const [cfg, setCfg] = useState(null);
  useEffect(() => {
    if (!_cfgPromise) _cfgPromise = api.getPrioCriteria().then(r => r.data).catch(() => ({ criteria: [], rules: null, descriptions: {}, rule: '' }));
    let alive = true;
    _cfgPromise.then(c => { if (alive) setCfg(c); });
    return () => { alive = false; };
  }, []);
  return cfg;
}

export const PrioBadge = ({ prio, className = '', testid = 'prio-badge', description }) => prio ? (
  <span data-testid={testid} title={description ? `${PRIO_LABEL(prio)} — ${description}` : prio === 'PASS' ? 'Kriterien reichen nicht für Prio B → Pass (Bench)' : `Deal-Prio ${prio}`}
    className={`inline-flex items-center text-[9px] font-mono font-bold border rounded px-1.5 py-0.5 uppercase shrink-0 ${PRIO_STYLES[prio] || 'border-border text-muted-foreground'} ${className}`}>
    {PRIO_LABEL(prio)}
  </span>
) : null;

// Checkbox list deciding the deal prio (A/B/C) — texts, "?" definitions and the rule come from the admin config.
export const PrioCriteriaPicker = ({ value, onChange }) => {
  const cfg = usePrioConfig();
  const criteria = cfg?.criteria;
  const counting = (criteria || []).filter(c => (c.kind || 'count') === 'count');
  const succession = (criteria || []).find(c => c.kind === 'succession');
  const knockouts = (criteria || []).filter(c => c.kind === 'knockout');
  const prio = computePrioWith(value, cfg?.rules, criteria);
  const toggle = (cid) => onChange(value.includes(cid) ? value.filter(x => x !== cid) : [...value, cid]);
  const countsFor = (cid) => ['A', 'B'].filter(p => (cfg?.rules?.[p]?.criteria || []).includes(cid));
  const Help = ({ c }) => c.definition ? (
    <Tooltip>
      <TooltipTrigger asChild>
        <button type="button" className="text-muted-foreground hover:text-foreground shrink-0" data-testid={`prio-criterion-${c.id}-help`} aria-label={`Definition: ${c.label}`}>
          <HelpCircle className="w-3.5 h-3.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs text-xs leading-snug" data-testid={`prio-criterion-${c.id}-definition`}>{c.definition}</TooltipContent>
    </Tooltip>
  ) : null;
  return (
    <TooltipProvider delayDuration={150}>
      <div className="border border-border rounded-sm p-3 space-y-2 bg-secondary/30" data-testid="prio-criteria-picker">
        <div className="flex items-center gap-2">
          <p className="text-xs font-medium text-foreground">Prio-Kriterien ankreuzen</p>
          <PrioBadge prio={prio} className="ml-auto" testid="prio-computed-badge" description={cfg?.descriptions?.[prio]} />
        </div>
        {!criteria ? <p className="text-[11px] text-muted-foreground">Lade Kriterien…</p> : (
          <div className="grid sm:grid-cols-2 gap-1.5">
            {counting.map(c => (
              <div key={c.id} className="flex items-center gap-2 text-xs text-foreground rounded-sm px-1 py-0.5 hover:bg-secondary/60">
                <label className="flex items-center gap-2 cursor-pointer flex-1 min-w-0">
                  <input type="checkbox" checked={value.includes(c.id)} onChange={() => toggle(c.id)} className="accent-amber-500"
                    data-testid={`prio-criterion-${c.id}`} />
                  <span className="truncate">{c.label}</span>
                </label>
                {countsFor(c.id).map(p => (
                  <span key={p} className={`text-[8px] font-mono font-bold border rounded px-1 ${PRIO_STYLES[p]}`} title={`Zählt für Prio ${p}`} data-testid={`prio-criterion-${c.id}-counts-${p}`}>{p}</span>
                ))}
                <Help c={c} />
              </div>
            ))}
          </div>
        )}
        {succession && (
          <div className={`border rounded-sm p-2 ${value.includes(succession.id) ? 'border-emerald-500/40 bg-emerald-500/5' : 'border-amber-500/40 bg-amber-500/5'}`} data-testid="prio-succession-block">
            <div className="flex items-center gap-2 text-xs text-foreground rounded-sm px-1 py-0.5">
              <label className="flex items-center gap-2 cursor-pointer flex-1 min-w-0">
                <input type="checkbox" checked={value.includes(succession.id)} onChange={() => toggle(succession.id)} className="accent-emerald-500"
                  data-testid={`prio-criterion-${succession.id}`} />
                <span className="truncate font-medium">{succession.label}</span>
              </label>
              <span className="text-[8px] font-mono font-bold border rounded px-1 border-emerald-500/50 bg-emerald-500/15 text-emerald-300" data-testid="prio-succession-marker"
                title="Nachfolge-Box: ohne Haken wird A zu C und B zu Pass (Bench)">Nachfolge</span>
              <Help c={succession} />
            </div>
            {!value.includes(succession.id) && <p className="text-[10px] text-amber-300/90 px-1" data-testid="prio-succession-hint">Nicht angekreuzt: A-Niveau wird Prio C (kein Nachfolgefall), B-Niveau wird Pass (Bench).</p>}
          </div>
        )}
        {knockouts.length > 0 && (
          <div className="border border-red-500/30 bg-red-500/5 rounded-sm p-2 space-y-1" data-testid="prio-knockout-block">
            <p className="text-[10px] uppercase tracking-wider font-bold text-red-300/90">Ausschluss-Kriterien</p>
            <div className="grid sm:grid-cols-2 gap-1.5">
              {knockouts.map(c => (
                <div key={c.id} className="flex items-center gap-2 text-xs text-foreground rounded-sm px-1 py-0.5 hover:bg-red-500/10">
                  <label className="flex items-center gap-2 cursor-pointer flex-1 min-w-0">
                    <input type="checkbox" checked={value.includes(c.id)} onChange={() => toggle(c.id)} className="accent-red-500"
                      data-testid={`prio-criterion-${c.id}`} />
                    <span className="truncate">{c.label}</span>
                  </label>
                  <span className="text-[8px] font-mono font-bold border rounded px-1 border-red-500/50 bg-red-500/15 text-red-300" data-testid={`prio-criterion-${c.id}-knockout`}
                    title="Angekreuzt: Deal ist ein Pass (Bench)">→ Pass</span>
                  <Help c={c} />
                </div>
              ))}
            </div>
          </div>
        )}
        <p className="text-[10px] text-muted-foreground" data-testid="prio-rule-text">{cfg?.rule || '≥ 4 Kriterien = A · 2–3 = B · darunter Pass'} — Prio steuert, wer den Brief schreibt und welche Outreach-Sequenz läuft (finale Freigabe immer durch den finalen Approver).</p>
        {prio === 'PASS' && <p className="text-[10px] text-zinc-300 border border-zinc-500/40 bg-zinc-500/10 rounded-sm px-2 py-1" data-testid="prio-pass-hint">Die Kriterien reichen nicht für Prio B — dieser Deal wird als Pass (Bench) vorgeschlagen, keine Freigabe.</p>}
        {prio !== 'PASS' && cfg?.descriptions?.[prio] && <p className="text-[10px] text-foreground/80" data-testid="prio-description">Prio {prio}: {cfg.descriptions[prio]}</p>}
      </div>
    </TooltipProvider>
  );
};
